import { z } from 'zod'

import {
  FORCE_CURRENT_USER,
  getEntityRegistry,
} from '../../engine/entity-registry'
import {
  collectControllingKeys,
  filterEnumByConstraints,
  normalizeControllingValues,
} from '../schema/codependent-enums'
import { requireDataClassName } from '../shared/entity-serializer'
import { getOrBuildSchema } from '../shared/schema-cache'
import type { AdapterReferenceFilter } from '@/Atomic/Widgets/AiChatBot/core/engine/contracts'
import { getHostRuntime } from '@/Atomic/Widgets/AiChatBot/core/engine/host-runtime'
import { defineTool } from '@/Atomic/Widgets/AiChatBot/core/engine/runtime/tool-system'

type WriteField = {
  key: string
  label: string
  type: string
  options?: Array<{ value: string; label: string }>
  targetClass?: string
  /**
   * For reference fields: the schema's referenceFilter (e.g. { isExternal: true }).
   * With targetClass it lets the model resolve the concrete class to search (Contact
   * + isExternal → an external contact / company) via search_entities before passing
   * the resolved _id — never a name.
   */
  referenceFilter?: AdapterReferenceFilter
  /**
   * For a polymorphic Contact reference (targetClass resolves to the base Contact,
   * i.e. no isPerson discriminator): tells the model Contact spans Company AND
   * external Person, so it stops assuming "person" and offering a Company-first
   * dance. Only set when targetClass === Contact.
   */
  targetClassNote?: string
  /** Other fields that must be set together with this one (conditionally-required pair/group). */
  requireTogether?: string[]
}

export const getWriteSchemaTool = defineTool({
  name: 'get_write_schema',
  cacheable: false,
  description:
    'Get the write-ready field list for creating or updating a CRM record. ' +
    'Returns requiredFields and optionalFields, each with key, label, type, and enum options where applicable. ' +
    'On create it may also return forcedFields — fields set automatically to a fixed value; NEVER ask the user for these or pass them to create_entity. ' +
    'On update it may return lockedFields — fields that exist but are frozen after creation (e.g. Activity "Main type"); if the user asks to change one, tell them it can only be set when the record is created, and do NOT confuse it with a similarly-named editable field (e.g. "Type"/subType). Never pass a lockedField to update_entity. ' +
    'Call this BEFORE create_entity or update_entity to know which fields to collect from the user. ' +
    'Some enum fields are constrained by another field (e.g. status depends on group; Activity subType depends on type). ' +
    'For updates, pass the record id and the controlling values are read from the record automatically; ' +
    'for creates, pass knownFields with any controlling values already chosen, so the returned enum options are pre-filtered to the valid ones. ' +
    'Offer the user ONLY the filtered options this tool returns for a constrained field. ' +
    'When to call again: for an UNCONSTRAINED field, reuse the options already returned in this conversation — never call again just to list them. ' +
    'For a CONSTRAINED field, options returned BEFORE the controlling value was known are the full unfiltered list and must never be reused — as soon as the controlling value becomes known or changes, call this tool AGAIN with that value in knownFields and offer only the freshly-filtered options. ' +
    'Never describe a field as free-text if it had options in a schema result. ' +
    'Do NOT use this for summary requests — use get_schema instead.',
  inputSchema: () =>
    z.object({
      dataClass: z
        .string()
        .describe('Entity type, e.g. Activity, Ticket, Opportunity'),
      operation: z
        .enum(['create', 'update'])
        .describe('Whether you are creating or updating a record'),
      id: z
        .string()
        .optional()
        .describe(
          'The _id of the record being updated. When provided (update), controlling field values (e.g. group) are read from the record to pre-filter dependent enum options like status.',
        ),
      knownFields: z
        .record(z.string(), z.string())
        .optional()
        .describe(
          'Controlling field values already chosen (e.g. { group: "PSA_PRS_LEA" }), used to filter dependent enum options like status. Merged with values read from the record when an id is given.',
        ),
    }),

  async run(args, ctx) {
    const { dataClass: rawDataClass, operation, id, knownFields } = args
    const dataClass = requireDataClassName(rawDataClass)
    if (!dataClass) {
      return { error: `Unknown entity: ${rawDataClass}` }
    }

    const meta = getEntityRegistry()[dataClass]

    if (operation === 'create' && !meta?.write?.create) {
      return { error: `Creating ${dataClass} records is not supported.` }
    }
    if (operation === 'update' && !meta?.write?.update) {
      return { error: `Updating ${dataClass} records is not supported.` }
    }

    const schema = await getOrBuildSchema(dataClass, ctx)
    if (!schema) {
      return { error: `Schema unavailable for ${dataClass}` }
    }

    // Resolve controlling values that pre-filter dependent enum options.
    // On update, read them from the record itself (the group/type/subType the
    // record already has) — the caller can't know them without fetching first.
    // Explicit knownFields win over record-derived values.
    let controllingValues = knownFields
    if (operation === 'update' && id) {
      const controllingKeys = collectControllingKeys(schema)
      if (controllingKeys.size > 0) {
        const item = await getHostRuntime().data.get(dataClass, id)
        // Fail fast on a bad/deleted/forbidden id — otherwise we'd hand the
        // model the unfiltered option list (all statuses across all groups) and
        // only surface the error later in update_entity. Mirrors its message.
        if (!item) {
          return { error: `${dataClass} with id ${id} not found.` }
        }
        // warning: adapter returns opaque unknown; record is a DataItem-like accessor with .get()
        const accessor = item as { get(f: string): unknown }
        const fromRecord: Record<string, string> = {}
        for (const key of controllingKeys) {
          const value = accessor.get(key)
          if (typeof value === 'string' && value.length > 0) {
            fromRecord[key] = value
          }
        }
        controllingValues = { ...fromRecord, ...knownFields }
      }
    }

    // The model passes controlling values by label (e.g. "Task"); the constraint
    // filter matches on codes (e.g. "TASK"). Resolve labels → codes first, else every
    // dependent option (e.g. Activity subType for a chosen type) filters out to empty.
    const normalizedControlling = controllingValues
      ? normalizeControllingValues(schema, controllingValues)
      : undefined

    // Fields set deterministically at create (e.g. List `type` = dynamic, `responsible`
    // = current user). Excluded from the prompted field list and surfaced separately so
    // the model knows they are already handled and never asks for or overrides them.
    const forced =
      operation === 'create'
        ? getEntityRegistry()[dataClass]?.forceAtCreate
        : undefined
    const forcedKeys = new Set(Object.keys(forced ?? {}))

    const requiredFields: WriteField[] = []
    const optionalFields: WriteField[] = []
    const forcedFields: Array<{ key: string; label: string; value: string }> =
      []
    // Update-only: fields that exist and are visible but frozen after creation
    // (e.g. Activity "Main type"). Surfaced so the model can tell the user the
    // field can't be changed, instead of misattributing the request to a
    // similarly-named editable field (Activity "Type" / subType).
    const lockedFields: Array<{ key: string; label: string }> = []

    // Map each field in a requireTogether group to its peers, so it can be tagged
    // "optional — set all or none" without being marked required.
    const requireTogetherByKey = new Map<string, string[]>()
    for (const group of meta?.fieldGroups ?? []) {
      for (const key of group.requireTogether) {
        requireTogetherByKey.set(
          key,
          group.requireTogether.filter((k) => k !== key),
        )
      }
    }

    for (const entry of schema.attributes) {
      const isWritable =
        operation === 'create' ? entry.creatable : entry.updatable
      if (!isWritable) {
        if (operation === 'update' && !forcedKeys.has(entry.name)) {
          lockedFields.push({
            key: entry.name,
            label: entry.title ?? entry.name,
          })
        }
        continue
      }

      if (forcedKeys.has(entry.name)) {
        const rawValue = forced?.[entry.name] ?? ''
        const valueLabel =
          rawValue === FORCE_CURRENT_USER
            ? 'current user'
            : (entry.values?.find((v) => v.value === rawValue)?.label ??
              rawValue)
        forcedFields.push({
          key: entry.name,
          label: entry.title ?? entry.name,
          value: valueLabel,
        })
        continue
      }

      const field: WriteField = {
        key: entry.name,
        label: entry.title ?? entry.name,
        type: entry.type,
      }

      if (
        (entry.type === 'enum' || entry.type === 'enumarray') &&
        entry.values &&
        entry.values.length > 0
      ) {
        // Filter co-dependent enum options (e.g. status by group) to the ones
        // valid for the controlling values (from the record on update, or
        // supplied by the caller on create).
        const valid = normalizedControlling
          ? filterEnumByConstraints(entry.values, normalizedControlling)
          : entry.values
        field.options = valid.map((e) => ({
          value: e.value,
          label: e.label,
        }))
      }

      if (entry.type === 'reference' && entry.targetClass) {
        // Resolve the concrete searchable class (Contact + isPerson → Person /
        // Company) so the model searches the right class directly, instead of the
        // ambiguous raw `referenceTo` ("Contact"). referenceFilter is still passed
        // through so search_entities can scope the sub-population (e.g. isExternal).
        const candidates =
          getHostRuntime().data.resolveReferenceCandidateClasses(
            entry.targetClass,
            entry.referenceFilter,
          )
        const [onlyCandidate] = candidates
        field.targetClass =
          candidates.length === 1 && onlyCandidate
            ? onlyCandidate
            : entry.targetClass
        if (candidates.length > 1) {
          field.targetClassNote =
            'Contact is polymorphic: this field accepts a Company OR an external Person. Search dataClass "Contact" with the referenceFilter to find either directly — do NOT assume it must be a person, and do NOT search Company first.'
        }
        if (entry.referenceFilter) {
          field.referenceFilter = entry.referenceFilter
        }
      }

      const peers = requireTogetherByKey.get(entry.name)
      if (peers) {
        field.requireTogether = peers
      }

      if (entry.mandatory) {
        requiredFields.push(field)
      } else {
        optionalFields.push(field)
      }
    }

    return {
      dataClass,
      operation,
      requiredFields,
      optionalFields,
      ...(forcedFields.length > 0 ? { forcedFields } : {}),
      ...(lockedFields.length > 0 ? { lockedFields } : {}),
      ...(meta?.fieldGroups && meta.fieldGroups.length > 0
        ? { fieldGroups: meta.fieldGroups }
        : {}),
      ...(meta?.fetchHints ? { fetchHints: meta.fetchHints } : {}),
    }
  },
})
